import { useQuery } from "@tanstack/react-query";
import { Link } from "raviger";
import { useTranslation } from "react-i18next";

import { ArrowRightIcon, FolderOpenIcon } from "@heroicons/react/24/outline";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import Page from "@/components/Common/Page";
import SearchInput from "@/components/Common/SearchInput";
import { CardGridSkeleton } from "@/components/Common/SkeletonLoading";

import useFilters from "@/hooks/useFilters";

import CareIcon from "@/CAREUI/icons/CareIcon";
import query from "@/Utils/request/query";
import {
  RESOURCE_REQUEST_ACTIVE_STATUSES,
  RESOURCE_REQUEST_COMPLETED_STATUSES,
  RESOURCE_REQUEST_STATUS_OPTIONS,
  ResourceRequestListRead,
  ResourceRequestStatus,
  getResourceRequestCategoryEnum,
} from "@/types/resourceRequest/resourceRequest";
import resourceRequestApi from "@/types/resourceRequest/resourceRequestApi";

function EmptyState() {
  const { t } = useTranslation();

  return (
    <Card className="flex flex-col items-center justify-center p-8 text-center border-dashed">
      <div className="rounded-full bg-primary/10 p-3 mb-4">
        <FolderOpenIcon className="size-6 text-primary" />
      </div>
      <h3 className="text-lg font-semibold mb-1">{t("no_resources_found")}</h3>
      <p className="text-sm text-gray-500 mb-4">
        {t("adjust_resource_filters")}
      </p>
    </Card>
  );
}

export default function ResourceList({ facilityId }: { facilityId: string }) {
  const { t } = useTranslation();

  const { qParams, updateQuery, Pagination, resultsPerPage } = useFilters({
    limit: 15,
    cacheBlacklist: ["title"],
  });
  const { status, title, incoming } = qParams;

  const isActive =
    !status || !RESOURCE_REQUEST_COMPLETED_STATUSES.includes(status);
  const currentStatuses = isActive
    ? RESOURCE_REQUEST_ACTIVE_STATUSES
    : RESOURCE_REQUEST_COMPLETED_STATUSES;

  // Set default status based on active/completed tab
  const defaultStatus = isActive
    ? ResourceRequestStatus.PENDING
    : ResourceRequestStatus.COMPLETED;
  const currentStatus = status || defaultStatus;

  const { data: queryResources, isLoading } = useQuery({
    queryKey: ["resources", facilityId, qParams],
    queryFn: query.debounced(resourceRequestApi.list, {
      queryParams: {
        status: currentStatus,
        title,
        limit: resultsPerPage,
        offset: ((qParams.page || 1) - 1) * resultsPerPage,
        ...(!incoming
          ? { origin_facility: facilityId }
          : { assigned_facility: facilityId }),
      },
    }),
  });

  const resources = queryResources?.results || [];

  return (
    <Page title={t("resource")} hideTitleOnPage>
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t("resource")}</h1>
          <Badge className="bg-purple-50 text-purple-700 mt-2 rounded-xl px-3 py-0.5 w-max border-gray-200">
            {isLoading
              ? t("loading")
              : t("entity_count", {
                  count: queryResources?.count ?? 0,
                  entity: "Resource",
                })}
          </Badge>
        </div>
        {/* AayushNet Logo - Right side */}
        <div className="flex items-center gap-0 shrink-0">
          <img
            src="/images/aayushnet_logo_light.png"
            className="h-12"
            alt="AayushNet logo"
          />
          <h1
            className="text-2xl font-black tracking-wider"
            style={{
              fontFamily: "'Zen Kaku Gothic Antique', sans-serif",
            }}
          >
            <span
              style={{
                background:
                  "linear-gradient(to bottom, #5ba3ff 0%, #002d6b 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                display: "inline-block",
                paddingBottom: "2px",
              }}
            >
              Aayush
            </span>
            <span
              style={{
                background:
                  "linear-gradient(to bottom, #66ff99 0%, #1a7a3f 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                display: "inline-block",
                paddingBottom: "2px",
              }}
            >
              Net
            </span>
          </h1>
        </div>
      </div>
      <div className="space-y-4 mt-4">
        <div className="border border-gray-200 rounded-lg">
          <div className="flex flex-col">
            <div className="flex flex-wrap items-center justify-between gap-2 p-4 sm:pb-4 pb-0">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                <SearchInput
                  className="w-full sm:w-48"
                  options={[
                    {
                      key: "title",
                      type: "text",
                      placeholder: t("search_by_resource_title"),
                      value: title || "",
                      display: t("title"),
                    },
                  ]}
                  onFieldChange={() => updateQuery({ title: undefined })}
                  onSearch={(key, value) =>
                    updateQuery({ [key]: value || undefined })
                  }
                />
                <div>
                  <Tabs value={incoming ? "incoming" : "outgoing"}>
                    <TabsList className="inline-flex bg-transparent p-0 h-8 w-full">
                      <TabsTrigger
                        value="outgoing"
                        className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary w-full"
                        onClick={() => updateQuery({ incoming: false })}
                      >
                        {t("outgoing")}
                      </TabsTrigger>
                      <TabsTrigger
                        value="incoming"
                        className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary w-full"
                        onClick={() => updateQuery({ incoming: true })}
                      >
                        {t("incoming")}
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
                <div className="sm:hidden block">
                  <Tabs value={isActive ? "active" : "completed"}>
                    <TabsList className="bg-transparent p-0 h-8 w-full">
                      <TabsTrigger
                        value="active"
                        className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary w-full"
                        onClick={() =>
                          updateQuery({ status: ResourceRequestStatus.PENDING })
                        }
                      >
                        {t("active")}
                      </TabsTrigger>
                      <TabsTrigger
                        value="completed"
                        className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary w-full"
                        onClick={() =>
                          updateQuery({
                            status: ResourceRequestStatus.COMPLETED,
                          })
                        }
                      >
                        {t("completed")}
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </div>
              <div className="hidden sm:block">
                <Tabs value={isActive ? "active" : "completed"}>
                  <TabsList className="bg-transparent p-0 h-8">
                    <TabsTrigger
                      value="active"
                      className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
                      onClick={() =>
                        updateQuery({ status: ResourceRequestStatus.PENDING })
                      }
                    >
                      {t("active")}
                    </TabsTrigger>
                    <TabsTrigger
                      value="completed"
                      className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
                      onClick={() =>
                        updateQuery({ status: ResourceRequestStatus.COMPLETED })
                      }
                    >
                      {t("completed")}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </div>

            <Separator className="hidden sm:block" />

            <div className="p-4 h-auto overflow-hidden">
              <div className="block sm:hidden w-full">
                <Select
                  value={currentStatus}
                  onValueChange={(value) => updateQuery({ status: value })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t("select_status")} />
                  </SelectTrigger>
                  <SelectContent>
                    {currentStatuses.map((statusOption) => (
                      <SelectItem key={statusOption} value={statusOption}>
                        {t(`resource_request_status__${statusOption}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Desktop Tabs */}
              <Tabs value={currentStatus} className="hidden sm:block w-full">
                <TabsList className="bg-transparent p-0 h-auto flex-wrap justify-start gap-y-2 overflow-auto">
                  {currentStatuses.map((statusOption) => (
                    <TabsTrigger
                      key={statusOption}
                      value={statusOption}
                      className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
                      onClick={() => updateQuery({ status: statusOption })}
                    >
                      <CareIcon
                        icon={
                          RESOURCE_REQUEST_STATUS_OPTIONS.find(
                            (o) => o.text === statusOption,
                          )?.icon || "l-folder-open"
                        }
                        className="size-4"
                      />
                      {t(`resource_request_status__${statusOption}`)}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
          {isLoading ? (
            <CardGridSkeleton count={6} />
          ) : resources.length === 0 ? (
            <div className="col-span-full">
              <EmptyState />
            </div>
          ) : (
            <>
              {resources.map(
                (resource: ResourceRequestListRead, index: number) => (
                  <Card
                    key={index}
                    className="hover:shadow-lg transition-shadow group flex flex-col justify-between"
                  >
                    <CardHeader className="space-y-1 pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="group-hover:text-primary transition-colors">
                          {resource.title}
                        </CardTitle>
                      </div>
                      <CardDescription className="line-clamp-2">
                        {resource.reason}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col space-y-2 px-6 py-0">
                      <div className="flex flex-wrap items-center gap-2">
                        {resource.emergency && (
                          <Badge variant="destructive">{t("emergency")}</Badge>
                        )}
                        <Badge variant="secondary">
                          {t(
                            `resource_request_category__${getResourceRequestCategoryEnum(resource.category)}`,
                          )}
                        </Badge>
                      </div>
                      <div className="flex flex-row gap-2">
                        <Badge variant="secondary">
                          {resource.origin_facility?.name}
                          <ArrowRightIcon className="mx-2 size-4" />
                          {resource.assigned_facility?.name}
                        </Badge>
                      </div>
                    </CardContent>
                    <CardFooter className="flex flex-col p-0">
                      <Separator className="my-2" />
                      <Link
                        href={`/facility/${resource.origin_facility.id}/resource/${resource.id}`}
                        className="items-center self-end pt-2 pr-4 pb-3 text-sm text-primary hover:underline text-right flex justify-end group-hover:translate-x-1 transition-transform"
                      >
                        {t("view_details")}
                        <ArrowRightIcon className="ml-1 size-4" />
                      </Link>
                    </CardFooter>
                  </Card>
                ),
              )}
              {queryResources?.count &&
                queryResources.count > resultsPerPage && (
                  <div className="col-span-full">
                    <Pagination totalCount={queryResources.count} />
                  </div>
                )}
            </>
          )}
        </div>
      </div>
    </Page>
  );
}
